import * as React from "react";
import { useState } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { validateExternalIdRequiresArn } from "integrations/aws/utils/awsValidation";
import useAWSRegions from "archive/hooks/useAWSRegions";
import { getExternalIdHelp, getS3Variant, getS3ProviderCopy } from "archive/logic/s3CompatibleProviders";
import { Button, ControlLabel, FormGroup, Input, SegmentedControl } from "components/bootstrap";
import { FormikFormGroup, Select } from "components/common";
import { STORAGE_REPOSITORY_TYPE_LABELS } from "data-tiering/repository-management/types";
const AUTH_OPTIONS = [
  { value: "automatic", labelKey: "s3_credentials.auth_automatic" },
  { value: "key_secret", labelKey: "s3_credentials.key_secret" }
];
const PROVIDER_OPTIONS = ["s3", "ovh", "syseleven"].map((provider) => ({
  value: provider,
  label: STORAGE_REPOSITORY_TYPE_LABELS[provider]
}));
export const validateS3CredentialsForm = (values) => {
  const errors = {};
  if (values.auth_mode === "key_secret") {
    if (!values.aws_access_key_id) {
      errors.aws_access_key_id = i18n.t("s3_credentials.key_secret_required", { ns: "enterprise_data_tiering" });
    }
    if (!values.aws_secret_access_key || "delete_value" in values.aws_secret_access_key) {
      errors.aws_secret_access_key = i18n.t("s3_credentials.key_secret_required", { ns: "enterprise_data_tiering" });
    }
  }
  const externalIdError = validateExternalIdRequiresArn(values.aws_assume_role_arn, values.aws_external_id);
  if (externalIdError) {
    errors.aws_external_id = externalIdError;
  }
  return errors;
};
const S3CredentialsFormFields = ({ secretIsSet }) => {
  const { t } = useTranslation("enterprise_data_tiering");
  const { values, errors, setFieldValue } = useFormikContext();
  const { data: regions } = useAWSRegions();
  const [showResetButton, setShowResetButton] = useState(secretIsSet);
  const variant = getS3Variant(values.provider);
  const copy = getS3ProviderCopy(variant);
  const authOptions = AUTH_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }));
  const secretError = errors.aws_secret_access_key;
  const setSecret = (secret) => setFieldValue("aws_secret_access_key", secret);
  const toggleSecretReset = () => {
    if (showResetButton) {
      setSecret({ delete_value: true });
      setShowResetButton(false);
    } else {
      setSecret({ keep_value: true });
      setShowResetButton(true);
    }
  };
  const handleAuthModeChange = (authMode) => {
    if (authMode === values.auth_mode) {
      return;
    }
    if (authMode === "automatic") {
      setFieldValue("aws_access_key_id", void 0);
      setSecret(void 0);
      setShowResetButton(false);
    } else {
      setSecret(secretIsSet ? { keep_value: true } : void 0);
      setShowResetButton(secretIsSet);
    }
    setFieldValue("auth_mode", authMode);
  };
  const handleProviderChange = (provider) => {
    if (provider === values.provider) {
      return;
    }
    setFieldValue("provider", provider);
    if (getS3Variant(provider) !== "s3") {
      handleAuthModeChange("key_secret");
      setFieldValue("aws_assume_role_arn", void 0);
      setFieldValue("aws_external_id", void 0);
    }
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, t("s3_form.storage_provider")), /* @__PURE__ */ React.createElement("div", { className: "col-sm-9" }, /* @__PURE__ */ React.createElement(SegmentedControl, { data: PROVIDER_OPTIONS, value: values.provider, onChange: handleProviderChange }))), variant === "s3" && /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials-auth-mode",
      label: t("s3_credentials.auth_type"),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "credentialsAuthMode",
        name: "auth_mode",
        placeholder: t("s3_credentials.auth_type_placeholder"),
        "aria-label": t("s3_credentials.auth_type_aria"),
        options: authOptions,
        onChange: handleAuthModeChange,
        clearable: false,
        value: values.auth_mode
      }
    )
  ), values.auth_mode === "key_secret" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "aws_access_key_id",
      type: "text",
      label: copy.accessKeyLabel,
      placeholder: t("s3_credentials.access_key_placeholder"),
      help: copy.accessKeyHelp,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  ), showResetButton ? /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials-secret-key-reset",
      label: copy.secretKeyLabel,
      error: secretError,
      bsStyle: secretError ? "error" : void 0,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(Button, { onClick: toggleSecretReset }, t("s3_form.reset_password"))
  ) : /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials-secret-key",
      type: "password",
      label: copy.secretKeyLabel,
      error: secretError,
      bsStyle: secretError ? "error" : void 0,
      onChange: ({ target: { value } }) => setSecret(value === "" ? { delete_value: true } : { set_value: value }),
      buttonAfter: secretIsSet ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: toggleSecretReset }, t("s3_form.undo_reset")) : void 0,
      placeholder: "****************",
      help: copy.secretKeyHelp,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  )), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials-aws-region",
      label: copy.regionLabel,
      error: errors.aws_region,
      bsStyle: errors.aws_region ? "error" : void 0,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      help: copy.regionHelp
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "credentialsAwsRegion",
        name: "aws_region",
        placeholder: t("s3_credentials.auth_automatic"),
        "aria-label": copy.regionAriaLabel,
        allowCreate: true,
        options: regions || [],
        onChange: (option) => setFieldValue("aws_region", option),
        value: values.aws_region
      }
    )
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "endpoint_uri",
      type: "text",
      label: copy.endpointLabel,
      help: copy.endpointHelp,
      error: errors.endpoint_uri,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  ), variant === "s3" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "aws_assume_role_arn",
      type: "text",
      label: t("s3_credentials.assume_role_label"),
      placeholder: t("s3_credentials.assume_role_placeholder"),
      help: t("s3_form.assume_role_help"),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "aws_external_id",
      type: "text",
      label: t("s3_credentials.external_id_label"),
      help: getExternalIdHelp(),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  )));
};
export default S3CredentialsFormFields;
